import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Sidebar from '../../components/sidebar/sidebar';
import ChatHeader from '../../components/chat/chat-header';
import ChatMessage from '../../components/chat/chat-message';
import ChatComposer from '../../components/chat/chat-composer';
import ReplyWaitIndicator from '../../components/chat/reply-wait-indicator';

import { useAppInfo, useQuery } from '../../helpers/hooks/common-hook';
import { useConversation } from '../../helpers/hooks/use-conversation';
import { useSessions } from '../../helpers/hooks/use-sessions';
import { getConfig } from '../../services/config';
import { defaultChatBotSettings } from '../../consts/settings';
import {
  resolveSessionId,
  rememberSessionId,
  startNewSession,
  upsertLocalSession,
} from '../../helpers/utils/session-util';
import { rememberUserId, resolveUserId } from '../../helpers/utils/user-util';

// Matches Tailwind's `lg` breakpoint, where the sidebar stops being an overlay
// drawer and becomes a column in the layout.
const DESKTOP_QUERY = '(min-width: 1024px)';

function decodeRequestAttributes(encoded) {
  if (!encoded) {
    return {};
  }
  try {
    return JSON.parse(atob(encoded));
  } catch (e) {
    return {};
  }
}

export default function ChatPage() {
  const query = useQuery();
  const { appId, accessKey } = useAppInfo();

  const sessionIdParam = query.get('session_id');
  const requestAttributes = useMemo(
    () => decodeRequestAttributes(query.get('request_attributes')),
    [query]
  );

  /* ---------------- visitor identity ----------------
   * Every request carries `requestAttributes.userId`; the runtime keys
   * persistent memory on it, so it has to be the same value next visit.
   *
   * A host page that already knows who the visitor is can pass a `userId`
   * inside `request_attributes`. Adopting it here persists it to the cookie,
   * so the identity survives a later visit that arrives without the param —
   * otherwise the visitor would silently split into two people. With no such
   * hint, an anonymous id is minted and reused from then on.
   */
  useEffect(() => {
    const hostUserId = requestAttributes?.userId;
    if (hostUserId) {
      rememberUserId(hostUserId);
    } else {
      resolveUserId();
    }
  }, [requestAttributes]);

  /* ---------------- session ----------------
   * Resolved once per visit: query param > cookie > freshly minted (which is
   * then written to the cookie). `isNew` decides whether to greet or to load
   * an existing transcript.
   *
   * Held in a ref rather than resolved inline in the useState initializer:
   * StrictMode invokes that initializer twice, and the second call would read
   * back the cookie the first one just wrote and conclude the session was
   * pre-existing - which suppressed the opening greeting.
   */
  const resolvedSessionRef = useRef(null);
  if (resolvedSessionRef.current === null) {
    const resolved = resolveSessionId(sessionIdParam);
    resolvedSessionRef.current = { id: resolved.sessionId, isNew: resolved.created };
  }

  const [session, setSession] = useState(resolvedSessionRef.current);

  // A `session_id` arriving later (link change) switches conversations.
  useEffect(() => {
    if (sessionIdParam && sessionIdParam !== session.id) {
      setSession({ id: sessionIdParam, isNew: false });
    }
  }, [sessionIdParam, session.id]);

  const [sessionRefresh, setSessionRefresh] = useState(0);

  /* ---------------- sidebar ----------------
   * Open by default on desktop, closed on mobile where it overlays the chat.
   * Crossing the breakpoint re-applies that default, so a window resized down
   * to phone width does not leave the drawer covering the transcript.
   */
  const [sidebarOpen, setSidebarOpen] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const apply = event => setSidebarOpen(event.matches);

    // Safari < 14 only has the deprecated addListener.
    if (media.addEventListener) {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    media.addListener(apply);
    return () => media.removeListener(apply);
  }, []);

  // Index the current session locally so it appears in the rail even before
  // the first message is exchanged.
  useEffect(() => {
    if (appId && session.id) {
      upsertLocalSession(appId, { sessionId: session.id });
      setSessionRefresh(token => token + 1);
    }
  }, [appId, session.id]);

  /* ---------------- settings ---------------- */
  const [chatBotSetting, setChatBotSetting] = useState(defaultChatBotSettings);

  useEffect(() => {
    let cancelled = false;

    async function loadSettings() {
      if (!appId) {
        return;
      }
      const resp = await getConfig(`app_${appId}`, 'web_integration', accessKey);
      if (!cancelled && resp && resp.data) {
        setChatBotSetting(previous => ({ ...previous, ...resp.data }));
      }
    }

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [appId, accessKey]);

  useEffect(() => {
    if (chatBotSetting.title) {
      document.title = chatBotSetting.title;
    }
    if (chatBotSetting.logo) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = chatBotSetting.logo;
    }
  }, [chatBotSetting]);

  /* ---------------- conversation ---------------- */
  const { messages, botReplyWait, historyLoading, sendMessage } = useConversation({
    appId,
    accessKey,
    sessionId: session.id,
    isNewSession: session.isNew,
    requestAttributes,
  });

  const { sessions, loading: sessionsLoading, unauthorized, reload } = useSessions({
    appId,
    accessKey,
    refreshToken: sessionRefresh,
  });

  /* ---------------- scrolling ---------------- */
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    // Only follow the conversation when the reader is already near the bottom,
    // so scrolling back through a long answer is not yanked away.
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    if (distanceFromBottom < 240) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, botReplyWait]);

  /* ---------------- actions ---------------- */

  const onSubmit = useCallback(
    question => {
      sendMessage(question);
      setSession(previous => ({ ...previous, isNew: false }));
    },
    [sendMessage]
  );

  const onInvokeAction = useCallback(
    (payload, attributes) => sendMessage(payload, attributes),
    [sendMessage]
  );

  const switchSession = useCallback(
    (sessionId, isNew) => {
      rememberSessionId(sessionId);
      setSession({ id: sessionId, isNew });
      setSessionRefresh(token => token + 1);
      // On mobile the drawer covers the chat, so picking a conversation should
      // reveal it.
      if (!window.matchMedia(DESKTOP_QUERY).matches) {
        setSidebarOpen(false);
      }
    },
    []
  );

  const onNewChat = useCallback(() => {
    switchSession(startNewSession(), true);
  }, [switchSession]);

  const onSelectSession = useCallback(
    selected => switchSession(selected.sessionId, false),
    [switchSession]
  );

  const showEmptyState = messages.length === 0 && !botReplyWait && !historyLoading;

  return (
    <div className="flex h-full w-full overflow-hidden bg-canvas">
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggle={() => setSidebarOpen(open => !open)}
        title={chatBotSetting.title}
        logo={chatBotSetting.logo}
        sessions={sessions}
        selectedSessionId={session.id}
        onSelectSession={onSelectSession}
        onNewChat={onNewChat}
        onRefreshSessions={reload}
        loading={sessionsLoading}
        unauthorized={unauthorized}
      />

      <main className="flex min-w-0 flex-1 flex-col">
        <ChatHeader
          title={chatBotSetting.subTitle || chatBotSetting.title}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(true)}
          onNewChat={onNewChat}
        />

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-reading px-4 pb-6">
            {showEmptyState && (
              <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
                <h2 className="text-2xl font-semibold tracking-tight text-ink">
                  {chatBotSetting.welcomeMessage}
                </h2>
                {chatBotSetting.tips?.length > 0 && (
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {chatBotSetting.tips.map((tip, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => onSubmit(tip)}
                        className="rounded-lg border border-hairline bg-surface px-3 py-2 text-[13px]
                          text-ink-muted transition-colors hover:border-accent/60 hover:text-ink"
                      >
                        {tip}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.map((message, index) => (
              <ChatMessage
                key={`${message.messageId}-${index}`}
                {...message}
                onInvokeAction={onInvokeAction}
                disabled={botReplyWait}
              />
            ))}

            {botReplyWait && <ReplyWaitIndicator />}
            <div ref={bottomRef} />
          </div>
        </div>

        <ChatComposer
          onSubmit={onSubmit}
          disabled={!appId || !session.id}
          placeholder={chatBotSetting.inputPlaceHolder}
        />
      </main>
    </div>
  );
}
